import { useAuth0 } from "@auth0/auth0-react";
import type { AlertColor } from "@mui/material/Alert/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { useState } from "react";

import { getGoogleOrgId } from "../../../api/apiConfig";
import useDevMode from "../../../utils/devmode";
import { AddPayerCredentials } from "../Form/AddPayerCredentials";
import type { CredentialsStore } from "../store";

interface NewCredentialsDialogProps {
  credentialsStore: CredentialsStore;
  practices: PracticeMessage[];
  supportedSources: SourceMessage[];
  supportedPMSes: PMSMessage[];
  afterFormSubmit: ({
    sourceId,
    practiceId,
    username,
    password,
  }: Partial<UserCredentialsMessage>) => Promise<void>;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  authorizeMfaEmail: () => Promise<void>;
}

export function NewCredentialsDialog({
  credentialsStore,
  practices,
  supportedSources,
  supportedPMSes,
  afterFormSubmit,
  updateSnackBar,
  authorizeMfaEmail,
}: NewCredentialsDialogProps) {
  const [open, setOpen] = useState(false);
  const devMode = useDevMode();
  const { user } = useAuth0();

  return (
    <>
      {(devMode || user?.org_id === getGoogleOrgId()) && (
        <Button variant="contained" onClick={authorizeMfaEmail}>
          Authorize Email
        </Button>
      )}
      <Button variant="contained" onClick={() => setOpen(true)}>
        Add Credential
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        sx={{
          "& .MuiPaper-root": {
            maxWidth: "735px",
            width: "100%",
            padding: "24px 36px",
            margin: "auto",
            borderRadius: "12px",
          },
        }}
      >
        <Box>
          <DialogContent sx={{ padding: 0, margin: 0 }}>
            <AddPayerCredentials
              userCredentials={credentialsStore.userCredentials}
              sources={supportedSources}
              pmses={supportedPMSes}
              practices={practices}
              cancel={() => {
                setOpen(false);
              }}
              afterFormSubmit={async ({
                sourceId,
                practiceId,
                username,
                password,
              }) => {
                setOpen(false);
                await afterFormSubmit({
                  sourceId,
                  practiceId,
                  username,
                  password,
                });
              }}
              updateSnackBar={updateSnackBar}
            />
          </DialogContent>
        </Box>
      </Dialog>
    </>
  );
}
